/**
 * Export Service
 * 거래 내역 내보내기 API 호출 및 파일 다운로드 처리
 */

import { apiClient } from './apiClient';

class ExportService {
  /**
   * 거래 내역 내보내기 요청
   * 백엔드에서 파일(blob)을 받아 브라우저 다운로드를 트리거한다.
   *
   * @param {object} options
   * @param {'csv'|'xlsx'} options.format - 파일 형식
   * @param {string} [options.startDate] - 시작일 (YYYY-MM-DD)
   * @param {string} [options.endDate] - 종료일 (YYYY-MM-DD)
   * @param {string[]} [options.categories] - 카테고리 필터 배열
   * @param {'income'|'expense'} [options.type] - 거래 유형 (전체면 생략)
   * @returns {Promise<void>}
   */
  async exportTransactions(options) {
    const params = new URLSearchParams();
    params.append('format', options.format);

    if (options.startDate) {
      params.append('startDate', options.startDate);
    }
    if (options.endDate) {
      params.append('endDate', options.endDate);
    }
    if (options.categories && options.categories.length > 0) {
      params.append('categories', options.categories.join(','));
    }
    if (options.type) {
      params.append('type', options.type);
    }

    const response = await apiClient.client.get(
      `/api/v1/export/transactions?${params.toString()}`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    const filename = this._extractFilename(response) || this._fallbackFilename(options.format);
    this._downloadBlob(response.data, filename);
  }

  /**
   * Content-Disposition 헤더에서 파일명 추출
   * @param {object} response - axios response
   * @returns {string|null}
   */
  _extractFilename(response) {
    const disposition = response.headers['content-disposition'];
    if (!disposition) return null;

    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    if (filenameMatch && filenameMatch[1]) {
      return decodeURIComponent(filenameMatch[1]);
    }
    return null;
  }

  /**
   * 기본 파일명 생성
   * @param {'csv'|'xlsx'} format
   * @returns {string}
   */
  _fallbackFilename(format) {
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    return `거래내역_${dateStr}.${ext}`;
  }

  /**
   * Blob을 브라우저 다운로드로 트리거
   * @param {Blob} blob
   * @param {string} filename
   */
  _downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const exportService = new ExportService();
export default exportService;
